import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';

// Lazy load the Gumlet player for better performance
const GumletPlayer = lazy(() =>
  import('@gumlet/react-embed-player').then(module => ({
    default: module.GumletPlayer
  }))
);

// Load Gumlet Insights SDK dynamically when video player mounts
function useGumletInsights() {
  useEffect(() => {
    // Check if already loaded
    if (typeof window === 'undefined' || (window as any).gumletInsights) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.gumlytics.com/insights/1.1/gumlet-insights.min.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      logger.debug('[GumletVideoPlayer] Gumlet Insights SDK loaded');
    };

    script.onerror = (error) => {
      logger.error('[GumletVideoPlayer] Failed to load Gumlet Insights SDK', error);
    };

    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      // Don't remove script - once loaded, keep it for other videos
    };
  }, []);
}

type GumletMode = 'user' | 'admin-test' | 'admin-preview';

interface GumletVideoPlayerProps {
  videoId: string;
  lessonId?: string;
  width?: string | number;
  height?: string | number;
  autoPlay?: boolean;
  className?: string;
  mode?: GumletMode; // default: 'user'
  title?: string; // For accessibility
  onVideoEnded?: () => void; // Called when video finishes playing
}

// SSR-safe device ID hook
function useDeviceId(): string | null {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  
  useEffect(() => {
    // Only access browser APIs in useEffect
    if (typeof window === 'undefined') return;
    
    try {
      const key = 'askjds_device_id';
      let id = localStorage.getItem(key);
      if (!id) {
        // Fallback for environments without crypto.randomUUID
        id = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(key, id);
        logger.debug('[GumletVideoPlayer] Created new device ID', { deviceId: id });
      }
      setDeviceId(id);
    } catch (err) {
      logger.error('[GumletVideoPlayer] Failed to get device ID', err);
      // Fallback device ID if localStorage fails
      setDeviceId(`fallback-${Date.now()}`);
    }
  }, []);
  
  return deviceId;
}

// Placeholder components
const PlayerSkeleton = () => (
  <div className="relative bg-gray-900 rounded-lg overflow-hidden animate-pulse" style={{ paddingBottom: '56.25%' }}>
    <div className="absolute inset-0 flex items-center justify-center">
      <LoadingSpinner className="w-12 h-12" />
    </div>
  </div>
);

const AccessDenied = ({ message = "You don't have access to this video" }: { message?: string }) => (
  <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg">
    <div className="flex items-start">
      <AlertCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
      <div>
        <p className="font-medium">Access Denied</p>
        <p className="text-sm mt-1 opacity-90">{message}</p>
      </div>
    </div>
  </div>
);


export function GumletVideoPlayer({
  videoId,
  lessonId,
  mode = 'user',
  width = '100%',
  height = 'auto',
  autoPlay = false,
  className = '',
  title = 'Course video',
  onVideoEnded,
  ...rest
}: GumletVideoPlayerProps) {
  const { user } = useAuth();
  const isAdmin = (user as any)?.user_metadata?.is_admin === true;
  const deviceId = useDeviceId();

  // Load Gumlet Insights SDK when video player mounts
  useGumletInsights();

  // Analytics tracking function for Gumlet events
  const trackGumletEvent = useCallback((event: string, payload: Record<string, unknown> = {}) => {
    const eventData = {
      event: `gumlet_${event}`,
      video_id: videoId,
      lesson_id: lessonId,
      user_id: user?.id,
      user_email: user?.email,
      device_id: deviceId,
      mode,
      ...payload
    };

    // Send to dataLayer for GTM if available
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push(eventData);
    }

    // Also log to our internal analytics
    logger.debug(`[GumletVideoPlayer] ${event}`, eventData);
  }, [videoId, lessonId, user?.id, user?.email, deviceId, mode]);

  // Log mode selection for debugging
  useEffect(() => {
    if (deviceId) {
      logger.debug('[GumletVideoPlayer] Rendering', { 
        videoId, 
        mode, 
        isAdmin,
        deviceId 
      });
    }
  }, [videoId, mode, isAdmin, deviceId]);

  // PostMessage listener for additional Gumlet communication
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from trusted Gumlet domains
      if (!/^https:\/\/(play|player)\.gumlet\.io$/.test(event.origin)) {
        return;
      }

      // Log the received message for debugging
      logger.debug('[GumletVideoPlayer] Received postMessage from Gumlet', {
        origin: event.origin,
        data: event.data,
        videoId,
        mode
      });

      // Forward interesting events to our analytics
      if (event.data && typeof event.data === 'object') {
        if (event.data.event) {
          trackGumletEvent('postmessage_event', {
            gumlet_event: event.data.event,
            gumlet_data: event.data,
            origin: event.origin
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [videoId, mode, trackGumletEvent]);

  // Validate video ID
  if (!videoId) {
    return (
      <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-8">
        <p className="text-gray-500 dark:text-gray-400">No video available</p>
      </div>
    );
  }

  // Wait for device ID (SSR safety)
  if (!deviceId) {
    return <PlayerSkeleton />;
  }

  // USER -> Gumlet embed player (access control is at the lesson page level)
  if (mode === 'user') {
    return (
      <div className={`relative ${className}`} style={{ width }}>
        <div
          className="relative w-full rounded-lg overflow-hidden"
          style={{
            paddingBottom: height === 'auto' ? '56.25%' : '0',
            height: height === 'auto' ? '0' : height
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full">
            <Suspense fallback={<PlayerSkeleton />}>
              <GumletPlayer
                videoID={videoId}
                title={title}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '100%',
                  objectFit: 'contain'
                }}
                autoplay={autoPlay}
                preload="metadata"
                muted={false}
                gm_user_id={user?.id}
                gm_user_email={user?.email}
                gm_user_name={user?.user_metadata?.full_name || user?.email?.split('@')[0]}
                gm_custom_data_1={lessonId}
                gm_custom_data_2={mode}
                gm_custom_data_3={deviceId}
                onReady={() => {
                  logger.debug('[GumletVideoPlayer] Player ready', { videoId, mode });
                  trackGumletEvent('player_ready', { player_type: 'user_embed' });
                }}
                onPlay={() => {
                  trackGumletEvent('player_play', { player_type: 'user_embed' });
                }}
                onPause={() => {
                  trackGumletEvent('player_pause', { player_type: 'user_embed' });
                }}
                onTimeupdate={(event: any) => {
                  const currentTime = event?.seconds || event?.currentTime || 0;
                  const duration = event?.duration || 0;
                  if (currentTime > 0 && currentTime % 30 === 0) {
                    trackGumletEvent('player_progress', {
                      current_time: currentTime,
                      duration,
                      progress_percent: duration > 0 ? Math.round((currentTime / duration) * 100) : 0,
                      player_type: 'user_embed'
                    });
                  }
                }}
                onEnded={() => {
                  trackGumletEvent('player_ended', { player_type: 'user_embed' });
                  onVideoEnded?.();
                }}
                onError={(error: any) => {
                  logger.error('[GumletVideoPlayer] Player error:', error);
                  trackGumletEvent('player_error', {
                    error: error?.message || 'Unknown error',
                    player_type: 'user_embed'
                  });
                }}
              />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  // ADMIN-TEST -> embed player without auth (for Gumlet branding)
  if (mode === 'admin-test') {
    if (!isAdmin) {
      return <AccessDenied message="Admin access required" />;
    }

    // Use lazy-loaded @gumlet/react-embed-player without edge function auth
    return (
      <div className={`relative ${className}`} style={{ width }}>
        <div 
          className="relative w-full rounded-lg overflow-hidden" 
          style={{ 
            paddingBottom: height === 'auto' ? '56.25%' : '0', 
            height: height === 'auto' ? '0' : height 
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full">
            <Suspense fallback={<PlayerSkeleton />}>
              <GumletPlayer
                videoID={videoId}
                title={title}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '100%',
                  objectFit: 'contain'
                }}
                autoplay={autoPlay}
                preload="metadata"
                muted={false}
                // Gumlet user identification for analytics
                gm_user_id={user?.id}
                gm_user_email={user?.email}
                gm_user_name={user?.user_metadata?.full_name || user?.email?.split('@')[0]}
                gm_custom_data_1={lessonId}
                gm_custom_data_2={mode}
                gm_custom_data_3={deviceId}
                // Event callbacks for analytics
                onReady={() => {
                  logger.debug('[GumletVideoPlayer] Admin player ready', { videoId, mode });
                  trackGumletEvent('player_ready', { player_type: 'admin_embed' });
                }}
                onPlay={() => {
                  trackGumletEvent('player_play', { player_type: 'admin_embed' });
                }}
                onPause={() => {
                  trackGumletEvent('player_pause', { player_type: 'admin_embed' });
                }}
                onTimeupdate={(event: any) => {
                  // Only track major time intervals to avoid spam
                  const currentTime = event?.seconds || event?.currentTime || 0;
                  const duration = event?.duration || 0;
                  if (currentTime > 0 && currentTime % 30 === 0) { // Every 30 seconds
                    trackGumletEvent('player_progress', { 
                      current_time: currentTime, 
                      duration,
                      progress_percent: duration > 0 ? Math.round((currentTime / duration) * 100) : 0,
                      player_type: 'admin_embed'
                    });
                  }
                }}
                onEnded={() => {
                  trackGumletEvent('player_ended', { player_type: 'admin_embed' });
                }}
                onError={(error: any) => {
                  logger.error('[GumletVideoPlayer] Admin player error:', error);
                  trackGumletEvent('player_error', { 
                    error: error?.message || 'Unknown error',
                    player_type: 'admin_embed'
                  });
                }}
              />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  // ADMIN-PREVIEW -> direct MP4 (fast)
  if (mode === 'admin-preview') {
    if (!isAdmin) {
      return <AccessDenied message="Admin access required for preview mode" />;
    }
    
    const collectionId = import.meta.env.VITE_GUMLET_JDS_COLLECTION_ID || '68434df22ea48d13d456a5ec';
    const mp4Url = `https://video.gumlet.io/${collectionId}/${videoId}/download.mp4`;
    const posterUrl = `https://video.gumlet.io/${collectionId}/${videoId}/thumbnail-1-0.png`;
    
    logger.debug('[GumletVideoPlayer] Using MP4 preview', { mp4Url, posterUrl });
    
    return (
      <video
        src={mp4Url}
        poster={posterUrl}
        controls
        controlsList="nodownload"
        preload="metadata"
        className={`rounded-lg ${className}`}
        style={{ width, height: height === 'auto' ? undefined : height }}
        aria-label={title}
        onLoadedMetadata={() => {
          logger.debug('[GumletVideoPlayer] MP4 preview ready', { videoId });
          // Emit telemetry
          logger.debug('[GumletVideoPlayer] admin_preview_start', { videoId, deviceId });
        }}
        onError={(e: any) => {
          logger.error('[GumletVideoPlayer] MP4 preview error', undefined, { type: e.type || 'media error' });
          // Emit telemetry
          logger.debug('[GumletVideoPlayer] admin_preview_error', { videoId, deviceId });
        }}
        {...rest}
      />
    );
  }

  return <AccessDenied message="Invalid player mode" />;
}

// Export types for external use
export type { GumletMode, GumletVideoPlayerProps };